import React, { Component } from 'react';
import { DatePicker, Icon } from '@anna/anna-ws-ui';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Table, Tooltip } from 'antd';
import { updateDates } from '../../services/transactionsService/actions';
import { updateSelectedAccountActions } from '../../services/accountService/actions';
import { getDefaultDates } from '../../services/transactionsService/reducer';

import styles from './styles.module.css';
import { gaSendUserEvent, gaEventName } from '../../utils/analytics';
import { DATE_FORMAT as UTILS_DATE_FORMAT } from '../../utils/formatters';

const DATE_FORMAT = 'DD MMM YYYY';

class Filter extends Component {
  getPredefinedDateRanges = () => {
    const ranges = {};
    const yearsBefore = 1;
    for (let i = 0; i <= yearsBefore; i += 1) {
      const date = moment().subtract(i, 'year');
      ranges[(date.year())] = this.getYearRange(date);
    }
    return ranges;
  };

  handleChangeDates = ([start, end]) => {
    const { actions, dates } = this.props;
    if (!start || !end) {
      const defaultDate = getDefaultDates();
      actions.updateDates({
        start: defaultDate.start,
        end: defaultDate.end,
      });
    } else {
      const startDate = start.set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      const endDate = end.set({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999,
      });
      actions.updateDates({
        start: startDate,
        end: endDate,
      });
    }
    gaSendUserEvent(
      gaEventName.selectDatetimeRange,
      `${dates.start.format(UTILS_DATE_FORMAT)}:${dates.end.format(UTILS_DATE_FORMAT)}`,
    );
  };

  toDateRangeArray = ({ start, end }) => [start, end];

  getYearRange = (date) => {
    const startDate = moment(date)
      .startOf('year');
    let endDate = moment(date)
      .endOf('year');
    if (endDate.year() === moment()
      .year()) {
      endDate = moment()
        .endOf('day');
    }
    return [startDate, endDate];
  };

  getDisabledDate = (currentDate) => {
    const { account } = this.props;

    if (account && account.created_at && account.source === 'anna' && !account.id.startsWith('expenses_')) {
      if (currentDate.set({
        hour: 23, minute: 59, second: 59, millisecond: 999,
      }) < new Date(account.created_at)) {
        return true;
      }
    }
    return currentDate && currentDate > moment().endOf('day');
  }

  getColorByIndex = (index) => {
    const colors = [
      '#E9624E',
      '#17233A',
      '#959595',
    ];
    return colors[index % colors.length];
  };

  gaSendUserEventByAccount = (account) => {
    if (account.source === 'anna') {
      gaSendUserEvent(gaEventName.selectAnnaAccount);
    } else if (account.source === 'openbanking') {
      gaSendUserEvent(gaEventName.selectOpenbankingAccount);
    } else if (account.source === 'expenses' && account.id === 'expenses_other_business_account') {
      gaSendUserEvent(gaEventName.selectExpensesOtherBusinessAccount);
    } else if (account.source === 'expenses' && account.id === 'expenses_business_cash') {
      gaSendUserEvent(gaEventName.selectExpensesBusinessCash);
    } else if (account.source === 'expenses' && account.id === 'expenses_paid_by_director') {
      gaSendUserEvent(gaEventName.selectExpensesPaidByDirector);
    } else if (account.source === 'manual' && account.id.endsWith('cash')) {
      gaSendUserEvent(gaEventName.selectManualAccountCash);
    } else if (account.source === 'manual' && account.id.endsWith('bank')) {
      gaSendUserEvent(gaEventName.selectManualAccountBank);
    } else if (account.source === 'manual' && account.id.endsWith('other')) {
      gaSendUserEvent(gaEventName.selectManualAccountOther);
    }
  };

  onAccountRowClick = (account) => {
    const { actions } = this.props;
    actions.updateSelectedAccount(account.id);
    this.gaSendUserEventByAccount(account);
  };

  render() {
    const { dates, accounts, account } = this.props;

    const columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'id',
        render: (value, record, index) => (
          <a
            href={`#${record.id}`}
            className={styles.AccountsTableLabel}
          >
            <div
              style={{ backgroundColor: this.getColorByIndex(index) }}
              className={styles.AccountsTableDot}
            />
            {record.title || record.name}
          </a>
        ),
      },
    ];

    const selectedAccounts = [];
    if (account) {
      selectedAccounts.push(account.id);
    }

    return (
      <div className={styles.SidePanel}>
        <div className={styles.FilterPanel}>
          <div className={styles.TimeFrameTitle}>
            <Tooltip
              title="We will recalculate the tax-related amounts when you change the timeframe"
              placement="right"
            >
              <Icon type="info-circle" theme="twoTone" style={{ marginRight: 5 }} />
            </Tooltip>
            Timeframe
          </div>
          <div className={styles.DatePicker}>
            <div className={styles.Test}>
              <DatePicker.RangePicker
                dates={dates}
                disabledDate={this.getDisabledDate}
                onChange={this.handleChangeDates}
                format={DATE_FORMAT}
                allowClear={false}
                ranges={this.getPredefinedDateRanges()}
                defaultValue={this.toDateRangeArray(dates)}
              />
            </div>
          </div>
          <div className={styles.AccountsTableTitle}>Accounts</div>
          <Table
            className={styles.AccountsTable}
            showHeader={false}
            pagination={false}
            columns={columns}
            dataSource={accounts}
            rowKey="id"
            onRow={(record, rowIndex) => ({
              onClick: () => this.onAccountRowClick(record, rowIndex),
            })}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedAccounts,
              selections: true,
            }}
          />
        </div>
      </div>
    );
  }
}

Filter.propTypes = {
  dates: PropTypes.shape({
    start: PropTypes.shape({
      format: PropTypes.func,
    }).isRequired,
    end: PropTypes.shape({
      format: PropTypes.func,
    }).isRequired,
  }).isRequired,
  accounts: PropTypes.arrayOf(PropTypes.shape({})),
  actions: PropTypes.shape({
    updateDates: PropTypes.func.isRequired,
    updateSelectedAccount: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    created_at: PropTypes.string,
    source: PropTypes.string,
    id: PropTypes.string,
  }),
};

Filter.defaultProps = {
  account: null,
  accounts: null,
};

const mapStateToProps = ({ transactionsService, accountService }) => ({
  dates: transactionsService.dates,
  selectedAccountNumber: transactionsService.selectedAccountNumber,
  accounts: accountService.accounts,
  account: accountService.account,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    updateDates,
    updateSelectedAccount: updateSelectedAccountActions.common,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Filter);
